import { Component, computed, input } from '@angular/core';
import { Candle } from '../core/models';
import { formatPrice } from '../core/format';

interface Bar {
  x: number;
  y: number;
  width: number;
  height: number;
  wickX: number;
  wickTop: number;
  wickBottom: number;
  up: boolean;
}

interface GridLine {
  y: number;
  label: string;
}

@Component({
  selector: 'app-candle-chart',
  template: `
    <svg viewBox="0 0 720 340" class="chart" role="img" aria-label="Candlestick chart">
      @for (line of frame().grid; track line.label) {
        <line [attr.x1]="12" [attr.x2]="640" [attr.y1]="line.y" [attr.y2]="line.y" class="grid" />
        <text [attr.x]="652" [attr.y]="line.y + 4" class="axis">{{ line.label }}</text>
      }
      @for (bar of frame().bars; track $index) {
        <line
          [attr.x1]="bar.wickX"
          [attr.x2]="bar.wickX"
          [attr.y1]="bar.wickTop"
          [attr.y2]="bar.wickBottom"
          [attr.stroke]="bar.up ? '#1c7a4d' : '#b42318'"
          stroke-width="1.2"
        />
        <rect
          [attr.x]="bar.x"
          [attr.y]="bar.y"
          [attr.width]="bar.width"
          [attr.height]="bar.height"
          [attr.fill]="bar.up ? '#1c7a4d' : '#b42318'"
          rx="1"
        />
      }
      @if (frame().lastY !== null) {
        <line [attr.x1]="12" [attr.x2]="640" [attr.y1]="frame().lastY" [attr.y2]="frame().lastY" class="last" />
      }
    </svg>
  `,
})
export class CandleChart {
  readonly candles = input<Candle[]>([]);
  readonly precision = input(2);

  protected readonly frame = computed(() => {
    const candles = this.candles();
    const empty = { bars: [] as Bar[], grid: [] as GridLine[], lastY: null as number | null };
    if (candles.length === 0) {
      return empty;
    }
    let min = Infinity;
    let max = -Infinity;
    for (const candle of candles) {
      min = Math.min(min, candle.low);
      max = Math.max(max, candle.high);
    }
    const pad = (max - min) * 0.1 || max * 0.01 || 1;
    min -= pad;
    max += pad;
    const top = 16;
    const plotH = 292;
    const plotW = 628;
    const yOf = (price: number) => top + ((max - price) / (max - min)) * plotH;
    const slot = plotW / candles.length;
    const bodyW = Math.max(2.4, slot * 0.58);
    const bars = candles.map((candle, index) => {
      const openY = yOf(candle.open);
      const closeY = yOf(candle.close);
      const y = Math.min(openY, closeY);
      const height = Math.max(1.2, Math.abs(closeY - openY));
      return {
        x: 12 + index * slot + (slot - bodyW) / 2,
        y,
        width: bodyW,
        height,
        wickX: 12 + index * slot + slot / 2,
        wickTop: yOf(candle.high),
        wickBottom: yOf(candle.low),
        up: candle.close >= candle.open,
      };
    });
    const grid = [0, 1, 2, 3].map((step) => {
      const price = max - ((max - min) * step) / 3;
      return { y: yOf(price), label: formatPrice(price, this.precision()) };
    });
    return { bars, grid, lastY: yOf(candles[candles.length - 1].close) };
  });
}
